/**
 * The body of an entity route module (web plan W4.4): the existing detail
 * page, given the entity the route's server loader read (@/hooks/entityPreload).
 * The server renders the page's real content from it (H1, breadcrumbs, text,
 * the entity's own offers and stores, and the secondary sections it read: the
 * brand hub's sale history / FAQ / similar brands, a promotion's rails, via
 * @/hooks/routePreload), the browser hydrates the same render,
 * and a signed-in viewer's own copy (their favourites and follows) loads after
 * mount (`useViewerState`). After an in-app navigation there is no server copy
 * (`clientLoader`): the page loads through Apollo as before.
 */
import { useMemo, type ReactNode } from 'react';
import { useLoaderData } from 'react-router';
import { EntityPreloadContext, type EntityPreload } from '@/hooks/entityPreload';
import { RoutePreloadContext, type RoutePreload } from '@/hooks/routePreload';
import { useViewerState } from '@/hooks/useViewerState';
import type { EntityRouteData } from '@/seo/entityDocument';

/** The brand hub has no viewer-specific fields: a signed-in viewer keeps the server's copy. */
const ANONYMOUS_ONLY_RAILS = ['BrandPageHub'] as const;

export function EntityRoute({ children }: { children: ReactNode }) {
  const loaded = useLoaderData() as EntityRouteData | undefined;
  const { isSignedIn } = useViewerState();
  const item = loaded?.entity ?? null;
  const preload = useMemo<EntityPreload | null>(
    () => (item ? { item, refresh: isSignedIn } : null),
    [item, isSignedIn],
  );
  // The secondary sections' answers (brand hub, promotion rails): read by
  // their hooks through `usePreloadedQuery`, like a list page's.
  const queries = item ? (loaded?.preload ?? null) : null;
  const rails = useMemo<RoutePreload | null>(
    () => (queries ? { queries, refresh: isSignedIn, anonymousOnly: ANONYMOUS_ONLY_RAILS } : null),
    [queries, isSignedIn],
  );
  return (
    <EntityPreloadContext.Provider value={preload}>
      <RoutePreloadContext.Provider value={rails}>{children}</RoutePreloadContext.Provider>
    </EntityPreloadContext.Provider>
  );
}

export default EntityRoute;
